import React from 'react';
import { Row, Col } from 'reactstrap';
import { OnChangeCallback } from '../../../../../../util/UtilityTypes'
import EnumInput from '../../../../../misc/EnumInput';
import BoolInput from '../../../../../misc/BoolInput';
import NullableInput from '../../../../../misc/NullableInput';
import IntInput from '../../../../../misc/IntInput';
import NullableIntInput from '../../../../../misc/NullableIntInput';
import * as GoogleUser from '../../../../../../util/GoogleUserExportTypes';
import { update } from '../../../../../../util/Helper';
import { useTranslation } from 'react-i18next';

interface GoogleUserEmailAddressPolicyProps {
    emailAddressPolicy: GoogleUser.EmailAddressPolicy
    configurationType: string
    disabled?: boolean
    validation: string[]
    onChange: OnChangeCallback<GoogleUser.EmailAddressPolicy>
}

const GoogleUserEmailAddressPolicy: React.FC<GoogleUserEmailAddressPolicyProps> = (props) => {
    const { t } = useTranslation(['configuration']);

    const renderOptions = () => {
        switch (props.emailAddressPolicy.type) {
            case GoogleUser.EmailAddressPolicyType.GENERATED:
                return (<>
                    <hr />
                    <h5>{t('googleUserExport.emailAddressPolicy.generated.title')}</h5>
                    <BoolInput
                        title={t('googleUserExport.emailAddressPolicy.generated.separateNamesByDots')}
                        name={`${props.configurationType}-emailAddressPolicy.separateNamesByDots`}
                        value={props.emailAddressPolicy.separateNamesByDots}
                        updateValue={(n, v) => update(n, props.emailAddressPolicy, { separateNamesByDots: v }, props.onChange)}
                        disabled={props.disabled}
                    />

                    <h5>{t('googleUserExport.emailAddressPolicy.generated.name.givenName.title')}</h5>
                    <Row>
                        <Col sm="2">
                            <BoolInput
                                title={t('googleUserExport.emailAddressPolicy.generated.name.givenName.include')}
                                name={`${props.configurationType}-emailAddressPolicy.includeGivenName`}
                                value={props.emailAddressPolicy.includeGivenName}
                                updateValue={(n, v) => update(n, props.emailAddressPolicy, { includeGivenName: v }, props.onChange)}
                                disabled={props.disabled}
                                titleSmall={true}
                            />
                        </Col>
                        <Col sm="10">
                            <NullableIntInput
                                title={t('googleUserExport.emailAddressPolicy.generated.name.characterCount')}
                                name={`${props.configurationType}-emailAddressPolicy.givenNameCharacterCount`}
                                value={props.emailAddressPolicy.givenNameCharacterCount}
                                disabled={props.disabled || !props.emailAddressPolicy.includeGivenName}
                                updateValue={(n, v) => update(n, props.emailAddressPolicy, { givenNameCharacterCount: v }, props.onChange)}
                                invalidFeedback={t('googleUserExport.emailAddressPolicy.generated.name.invalidCharacterCount')}
                                invalid={props.validation.indexOf(`${props.configurationType}.emailAddressPolicy.givenNameCharacterCount`) !== -1}
                            />
                        </Col>
                    </Row>

                    <h5>{t('googleUserExport.emailAddressPolicy.generated.name.familyName.title')}</h5>
                    <Row>
                        <Col sm="2">
                            <BoolInput
                                title={t('googleUserExport.emailAddressPolicy.generated.name.familyName.include')}
                                name={`${props.configurationType}-emailAddressPolicy.includeFamilyName`}
                                value={props.emailAddressPolicy.includeFamilyName}
                                updateValue={(n, v) => update(n, props.emailAddressPolicy, { includeFamilyName: v }, props.onChange)}
                                disabled={props.disabled}
                                titleSmall={true}
                            />
                        </Col>
                        <Col sm="10">
                            <NullableIntInput
                                title={t('googleUserExport.emailAddressPolicy.generated.name.characterCount')}
                                name={`${props.configurationType}-emailAddressPolicy.familyNameCharacterCount`}
                                value={props.emailAddressPolicy.familyNameCharacterCount}
                                disabled={props.disabled || !props.emailAddressPolicy.includeFamilyName}
                                updateValue={(n, v) => update(n, props.emailAddressPolicy, { familyNameCharacterCount: v }, props.onChange)}
                                invalidFeedback={t('googleUserExport.emailAddressPolicy.generated.name.invalidCharacterCount')}
                                invalid={props.validation.indexOf(`${props.configurationType}.emailAddressPolicy.familyNameCharacterCount`) !== -1}
                            />
                        </Col>
                    </Row>

                    {props.validation.indexOf(`${props.configurationType}.emailAddressPolicy.nameCharacterTotalCount`) !== -1 &&
                        <p className="red">{t('googleUserExport.emailAddressPolicy.generated.name.invalidTotalCount')}</p>}

                    <h5>{t('googleUserExport.emailAddressPolicy.generated.serialNumber.title')}</h5>
                    <Row>
                        <Col sm="2">
                            <BoolInput
                                title={t('googleUserExport.emailAddressPolicy.generated.serialNumber.useSerialNumber')}
                                name={`${props.configurationType}-emailAddressPolicy.useSerialNumber`}
                                value={props.emailAddressPolicy.useSerialNumber}
                                updateValue={(n, v) => update(n, props.emailAddressPolicy, { useSerialNumber: v }, props.onChange)}
                                disabled={true}
                                titleSmall={true}
                            />
                        </Col>
                        <Col sm="10">
                            <NullableInput
                                title={t('googleUserExport.emailAddressPolicy.generated.serialNumber.prefix')}
                                name={`${props.configurationType}-emailAddressPolicy.serialNumberDelimiter`}
                                value={props.emailAddressPolicy.serialNumberDelimiter}
                                onChange={(n, v) => update(n, props.emailAddressPolicy, { serialNumberDelimiter: v }, props.onChange)}
                                disabled={props.disabled || !props.emailAddressPolicy.useSerialNumber}
                                invalidFeedback={t('googleUserExport.emailAddressPolicy.generated.serialNumber.invalidPrefix')}
                                invalid={props.validation.indexOf(`${props.configurationType}.emailAddressPolicy.serialNumberDelimiter`) !== -1}
                                titleSmall={true}
                            />
                        </Col>
                    </Row>
                </>);
            case GoogleUser.EmailAddressPolicyType.ANONYMOUS:
                return (<>
                    <hr />
                    <h5>{t('googleUserExport.emailAddressPolicy.anonymous.title')}</h5>
                    <IntInput
                        title={t('googleUserExport.emailAddressPolicy.anonymous.randomCharacter')}
                        name={`${props.configurationType}-emailAddressPolicy.randomCharacterCount`}
                        value={props.emailAddressPolicy.randomCharacterCount}
                        disabled={props.disabled}
                        updateValue={(n, v) => update(n, props.emailAddressPolicy, { randomCharacterCount: v }, props.onChange)}
                        invalidFeedback={t('googleUserExport.emailAddressPolicy.anonymous.invalidCharacted')}
                        invalid={props.validation.indexOf(`${props.configurationType}.emailAddressPolicy.randomCharacterCount`) !== -1}
                    />
                    <IntInput
                        title={t('googleUserExport.emailAddressPolicy.anonymous.randomNumber')}
                        name={`${props.configurationType}-emailAddressPolicy.randomNumberCount`}
                        value={props.emailAddressPolicy.randomNumberCount}
                        disabled={props.disabled}
                        updateValue={(n, v) => update(n, props.emailAddressPolicy, { randomNumberCount: v }, props.onChange)}
                        invalidFeedback={t('googleUserExport.emailAddressPolicy.anonymous.invalidNumber')}
                        invalid={props.validation.indexOf(`${props.configurationType}.emailAddressPolicy.randomNumberCount`) !== -1}
                    />

                    {props.validation.indexOf(`${props.configurationType}.emailAddressPolicy.randomTotalCount`) !== -1 &&
                        <p className="red">{t('googleUserExport.emailAddressPolicy.anonymous.invalidTotalCount')}</p>}

                </>);
            default: return null;
        }
    }

    return (<>
        <EnumInput
            title={t('googleUserExport.emailAddressPolicy.title')}
            name={`${props.configurationType}-emailAddressPolicy.type`}
            value={props.emailAddressPolicy.type}
            options={GoogleUser.emailAddressPolicyTypeOptions.map(x => ({ value: x, text: t(`googleUserExport.emailAddressPolicy.emailAddressPolicyTypeOptions.${x}`) }))}
            onChange={(n, v) => update(n, props.emailAddressPolicy, { type: v }, props.onChange)}
            disabled={props.disabled}
        />

        {renderOptions()}

        <hr />
        <NullableInput
            title={t('googleUserExport.emailAddressPolicy.domainName.title')}
            titleSmall={false}
            onChange={(n, v) => update(n, props.emailAddressPolicy, { domainName: v }, props.onChange)}
            value={props.emailAddressPolicy.domainName}
            disabled={props.disabled}
            name={`${props.configurationType}-emailAddressPolicy.domainName`}
            invalidFeedback={t('googleUserExport.emailAddressPolicy.domainName.invalidFeedback')}
            invalid={props.validation.indexOf(`${props.configurationType}.emailAddressPolicy.domainName`) !== -1}
        />
    </>);
}

export default GoogleUserEmailAddressPolicy; 
